import apiClient from '../lib/api-client';

export interface LoginRequest {
  email: string;
  password: string;
}

export interface LoginResponse {
  token: string;
}

export interface User {
  id: string;
  name: string;
  email: string;
  role: string;
  roles?: string[]; // Hỗ trợ cả roles array
}

export interface ApiError {
  message: string;
  code?: string;
  status?: number;
}

class AuthService {
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    try {
      const response = await apiClient.post<LoginResponse>('/auth/login', credentials);
      return response.data;
    } catch (error: any) {
      // Handle axios error response
      if (error.response?.data) {
        const errorData = error.response.data;
        
        // Handle admin role required error specifically
        if (errorData.error_code === 'ADMIN_ROLE_REQUIRED') {
          throw {
            message: 'Chỉ có quyền admin mới được đăng nhập vào hệ thống',
            code: errorData.error_code,
            status: error.response.status
          } as ApiError;
        }
        
        throw {
          message: errorData.message || 'Đăng nhập thất bại',
          code: errorData.code,
          status: error.response.status
        } as ApiError;
      }
      
      // Handle network or other errors
      throw {
        message: 'Không thể kết nối đến máy chủ',
        status: 0
      } as ApiError;
    }
  }

  async getCurrentUserInfo(): Promise<User> {
    try {
      const response = await apiClient.get<{ data: User }>('/auth/me');
      return response.data.data;
    } catch (error: any) {
      if (error.response?.status === 401) {
        // Token không hợp lệ, clear local storage
        localStorage.removeItem('auth_token');
        localStorage.removeItem('user');
        throw {
          message: 'Phiên đăng nhập đã hết hạn',
          code: 'TOKEN_EXPIRED',
          status: 401
        } as ApiError;
      }
      
      if (error.response?.data) {
        throw {
          message: error.response.data.message || 'Không thể lấy thông tin user',
          code: error.response.data.code,
          status: error.response.status
        } as ApiError;
      }
      
      throw {
        message: 'Không thể kết nối đến máy chủ',
        status: 0
      } as ApiError;
    }
  }

  async logout(): Promise<void> {
    try {
      await apiClient.post('/auth/logout');
    } catch (error) {
      // Log logout error but don't throw
      console.warn('Logout API call failed:', error);
    } finally {
      // Always clear local storage
      localStorage.removeItem('auth_token');
      localStorage.removeItem('user');
    }
  }

  getCurrentUser(): User | null {
    const userStr = localStorage.getItem('user');
    return userStr ? JSON.parse(userStr) : null;
  }

  getAuthToken(): string | null {
    return localStorage.getItem('auth_token');
  }

  isAuthenticated(): boolean {
    const token = this.getAuthToken();
    const user = this.getCurrentUser();
    return !!(token && user);
  }

  // Helper method để lấy roles từ user
  getUserRoles(user: User | null): string[] {
    if (!user) return [];
    
    // Nếu có roles array, sử dụng nó
    if (user.roles && Array.isArray(user.roles)) {
      return user.roles;
    }
    
    // Nếu chỉ có role string, chuyển thành array
    if (user.role) {
      return [user.role];
    }
    
    return [];
  }

  async changePassword(currentPassword: string, newPassword: string): Promise<void> {
    await apiClient.post('/auth/change-password', {
      current_password: currentPassword,
      new_password: newPassword,
    });
  }
}

export default new AuthService(); 